/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Field,
  FormDataType,
  Section,
  ProfileProps,
} from '@tw/tw-component-library';
import { SectionKey } from '../../../../../types';
import { GroupedSection } from 'node_modules/@tw/tw-component-library/dist/types/types.index';

// ============= Basic Types =============
export type FormatterValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<any>;

export type ValidationRuleType =
  | 'required'
  | 'min'
  | 'max'
  | 'pattern'
  | 'custom'
  | 'email'
  | 'url';
export type DependencyType =
  | 'enable'
  | 'disable'
  | 'show'
  | 'hide'
  | 'require'
  | 'validate'
  | 'section';
export type CalculationType =
  | 'sum'
  | 'multiply'
  | 'subtract'
  | 'divide'
  | 'custom';
export type OperatorType =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'greaterThan'
  | 'lessThan'
  | 'notContains'
  | 'isEmpty'
  | 'isNotEmpty';

// ============= Formatter Types =============
export interface FieldFormatters {
  input: (value: FormatterValue) => FormatterValue;
  output: (value: FormatterValue) => FormatterValue;
  display: (value: FormatterValue) => string;
}

export interface FormatterOptions {
  numberFormat?: Intl.NumberFormatOptions;
  dateFormat?: Intl.DateTimeFormatOptions;
  locale?: string;
  currency?: string;
}

// ============= Validation Types =============
export interface ValidationRule {
  type: ValidationRuleType;
  value?: number | string | RegExp;
  message: string;
  customValidator?: (
    value: any,
    formData: FormDataType
  ) => boolean | Promise<boolean>;
}

export interface ValidationError {
  field: string;
  message: string;
  type: ValidationRuleType;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// ============= Dependency Types =============
export interface DependencyCondition {
  field: string;
  operator: OperatorType;
  value: any;
}

export interface FieldDependency {
  type: DependencyType;
  conditions: DependencyCondition[];
  logicalOperator?: 'AND' | 'OR'; // Default to AND if not specified
  message?: string;
}

// ============= Calculation Types =============
export interface CalculationRule {
  type: CalculationType;
  sourceFields: string[];
  customCalculation?: (values: any[], formData: FormDataType) => number;
  roundTo?: number;
  formatOptions?: Intl.NumberFormatOptions;
}

// ============= Error Message Types =============
export interface ErrorMessages {
  required?: string;
  pattern?: string;
  min?: string;
  max?: string;
  email?: string;
  url?: string;
  custom?: string;
  [key: string]: string | undefined;
}

// ============= Enhanced Field Types =============
export interface EnhancedFieldConfig extends Field {
  validation?: ValidationRule[];
  calculations?: CalculationRule;
  formatters?: FieldFormatters;
  value?: FormatterValue;
  errorMessages?: ErrorMessages;
  hidden?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  defaultValue?: any;
}

// ============= Enhanced Section Types =============
/**
 * Enhanced Field Configuration
 *
 * @interface EnhancedFieldConfig
 *
 * Extends the base Field type with additional validation and formatting capabilities
 *
 * @property {ValidationRule[]} [validation] - Array of validation rules
 * @property {FieldDependency[]} [dependencies] - Field dependencies
 * @property {CalculationRule} [calculations] - Calculation rules
 * @property {FieldFormatters} [formatters] - Input/output formatters
 * @property {FormatterValue} [value] - Current field value
 * @property {ErrorMessages} [errorMessages] - Custom error messages
 * @property {boolean} [hidden] - Field visibility
 * @property {boolean} [disabled] - Field enabled/disabled state
 * @property {boolean} [readOnly] - Field read-only state
 * @property {any} [defaultValue] - Default field value
 *
 * @example
 * ```typescript
 * const fieldConfig: EnhancedFieldConfig = {
 *   name: "email",
 *   type: "email",
 *   validation: [{
 *     type: "email",
 *     message: "Please enter a valid email"
 *   }],
 *   formatters: {
 *     input: (value) => value?.toLowerCase(),
 *     output: (value) => value,
 *     display: (value) => value || "No email provided"
 *   }
 * };
 * ```
 */

/**
 * Example Usage
 *
 * @example
 * ```typescript
 * // Basic usage in a component
 * const MyFormComponent = () => {
 *   const formState = useFormState();
 *   const updateFormData = useCallback((data) => {
 *     // Update logic
 *   }, []);
 *
 *   const schemas = useSchemas(formState, updateFormData);
 *
 *   // Use schemas for validation
 *   const validatePaymentDetails = () => {
 *     const schema = schemas.payment_details;
 *     // Validation logic
 *   };
 *
 *   return (
 *     // Form JSX
 *   );
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Accessing specific section schema
 * const PaymentSection = () => {
 *   const { payment_details } = useSchemas(formState, updateFormData);
 *
 *   // Use payment_details schema for rendering and validation
 *   return (
 *     // Section JSX
 *   );
 * };
 * ```
 */
export interface EnhancedSection extends Omit<Section, 'fields'> {
  fields: EnhancedFieldConfig[];
  /**
   * Used as accordion/group header when nested in a grouped parent.
   * Content heading is off by default; set `showContentTitle: true` to also
   * render it inside the wrapper content.
   */
  title?: Section['title'];
  showContentTitle?: boolean;
  description?: string;
  hidden?: boolean;
  disabled?: boolean;
  dependencies?: FieldDependency[];
}

export interface EnhancedGroupedSection
  extends Omit<GroupedSection, 'sections'> {
  sections: (EnhancedSection | EnhancedGroupedSection)[];
  /**
   * Parent grouped titles are not rendered by FormStack — only child section
   * `title.text` values are used as accordion/group headers.
   */
  title?: Section['title'];
  profile?: ProfileProps;
  description?: string;
  hidden?: boolean;
  disabled?: boolean;
  dependencies?: FieldDependency[];
}

// ============= Schema Configuration Types =============
export interface SchemaOrderConfig {
  priority: number;
  dependencies?: SectionKey[];
  validationOrder?: number;
  calculationOrder?: number;
  affectedSections?: SectionKey[];
}
export interface ValidationConfig {
  dependencies?: Record<string, FieldDependency[]>;
  rules?: Record<string, ValidationRule[]>;
}
/**
 * Form Schema Configuration
 *
 * @interface FormSchemaConfig
 *
 * Defines the structure and validation rules for a form section
 *
 * @property {(EnhancedSection | EnhancedGroupedSection)[]} sections - Array of form sections
 * @property {ValidationConfig} [validation] - Validation rules and dependencies
 * @property {Record<string, CalculationRule>} [calculations] - Field calculation rules
 * @property {Record<string, FieldFormatters>} [formatters] - Field formatting functions
 * @property {Record<string, ErrorMessages>} [errorMessages] - Custom error messages
 *
 * @example
 * ```typescript
 * const sectionSchema: FormSchemaConfig = {
 *   sections: [{
 *     title: "Payment Details",
 *     fields: [
 *       {
 *         name: "amount",
 *         type: "number",
 *         validation: [{
 *           type: "required",
 *           message: "Amount is required"
 *         }]
 *       }
 *     ]
 *   }]
 * };
 * ```
 */
export interface FormSchemaConfig {
  sections: (EnhancedSection | EnhancedGroupedSection)[];
  validation?: ValidationConfig;
  calculations?: Record<string, CalculationRule>;
  formatters?: Record<string, FieldFormatters>;
  errorMessages?: Record<string, ErrorMessages>;
  order?: SchemaOrderConfig;
  dependencies?: {
    showWhen?: Array<{ field: string }>;
  };
}

// ============= Context Types =============
export interface FormConfigContextValue {
  formSchemas: Record<SectionKey, FormSchemaConfig>;
  processFields?: (fields: EnhancedFieldConfig[]) => EnhancedFieldConfig[];
  processSections?: (
    sections: (EnhancedSection | EnhancedGroupedSection)[]
  ) => (EnhancedSection | EnhancedGroupedSection)[];
  validateSection?: (
    section: SectionKey,
    schema: FormSchemaConfig,
    data: FormDataType
  ) => ValidationResult;
}

// ============= Type Guards =============
export const isEnhancedSection = (
  section: EnhancedSection | EnhancedGroupedSection
): section is EnhancedSection => {
  return 'fields' in section;
};

export const isEnhancedGroupedSection = (
  section: EnhancedSection | EnhancedGroupedSection
): section is EnhancedGroupedSection => {
  return 'sections' in section;
};

export const isValidationRule = (rule: any): rule is ValidationRule => {
  return (
    rule && typeof rule === 'object' && 'type' in rule && 'message' in rule
  );
};

export const isFieldDependency = (
  dependency: any
): dependency is FieldDependency => {
  return (
    dependency &&
    typeof dependency === 'object' &&
    'field' in dependency &&
    'type' in dependency &&
    'condition' in dependency
  );
};

// ============= Default Values =============
export const DEFAULT_ERROR_MESSAGES: ErrorMessages = {
  required: 'This field is required',
  pattern: 'Invalid format',
  min: 'Value is too small',
  max: 'Value is too large',
  email: 'Invalid email address',
  url: 'Invalid URL',
  custom: 'Invalid value',
};

export const DEFAULT_FORMATTERS: FieldFormatters = {
  input: (value: FormatterValue) => value,
  output: (value: FormatterValue) => value,
  display: (value: FormatterValue) => String(value ?? ''),
};

export const EMPTY_SCHEMA: FormSchemaConfig = {
  sections: [],
};
